import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, type ReactNode } from 'react';

import { useProfileNavSection } from '@/core/store/profileNavigation/profileNavigationSelectors';
import { mergeSx } from '@/utils/mergeSx';

type Props = {
	id?: string;
	title: string;
	children: ReactNode;
	hideHeader?: boolean;
	controls?: ReactNode;
	customSx?: {
		root?: SxProps<Theme>;
		title?: SxProps<Theme>;
		section?: SxProps<Theme>;
	};
};

export const SectionBlock = ({
	id,
	title,
	children,
	hideHeader = false,
	controls,
	customSx
}: Props) => {
	const { addSection, removeSection } = useProfileNavSection();

	useEffect(() => {
		addSection({
			id: id ?? title,
			title: title
		});

		return () => {
			removeSection(id ?? title);
		};
	}, [id, title, addSection, removeSection]);

	return (
		<Box sx={mergeSx({ my: 0, px: 2 }, customSx?.root)} id={id ?? title}>
			{!hideHeader && (
				<Box
					display='flex'
					flexDirection='row'
					alignItems='center'
					justifyContent='space-between'
				>
					<Typography
						variant='subtitle1'
						fontWeight={700}
						fontSize={'1.25rem'}
						color='text.fadedPrimary'
						lineHeight={'160%'}
						letterSpacing={'0.15px'}
						mb={1}
						sx={customSx?.title}
					>
						{title}
					</Typography>
					{controls}
				</Box>
			)}
			<Box display='flex' flexDirection='column' gap={2} sx={customSx?.section}>
				{children}
			</Box>
		</Box>
	);
};
